'use client';

/**
 * TCO Workflow Dashboard Component
 *
 * Dashboard for monitoring and managing TCO agentic workflows including:
 * - Active workflow instances
 * - Workflow templates
 * - Statistics and metrics
 * - Workflow initiation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Search,
  RefreshCw,
  Plus,
  Bot,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Users,
  Zap,
  BarChart3,
  Settings,
  ChevronRight,
  Loader2,
  Filter,
  Activity,
  FileText,
  Heart,
  UserCheck,
  Calendar,
  Wand2,
} from 'lucide-react';
import { AutomationRules } from '@/components/workflows/AutomationRules';
import {
  tcoWorkflowsAPI,
  WorkflowTemplate,
  WorkflowInstance,
  DashboardStats,
} from '@/services/api/tcoServiceReferrals';
import { useToast } from '@/components/ui/use-toast';
import { useTabbedChrome } from '@/components/weavein';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TCOWorkflowDashboardProps {
  className?: string;
  onViewInstance?: (instanceId: number) => void;
  onEditTemplate?: (templateId: number) => void;
  onCreateTemplate?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TCOWorkflowDashboard({
  className = '',
  onViewInstance,
  onEditTemplate,
  onCreateTemplate,
}: TCOWorkflowDashboardProps) {
  const { toast } = useToast();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const tabbed = useTabbedChrome();

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'instances' | 'templates' | 'automations'>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Track if initial load has been attempted
  const [hasLoadError, setHasLoadError] = useState(false);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setHasLoadError(false);
    try {
      const [statsData, instancesData, templatesData] = await Promise.all([
        tcoWorkflowsAPI.getDashboard(),
        tcoWorkflowsAPI.listInstances({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        tcoWorkflowsAPI.listTemplates({ is_active: true }),
      ]);

      setStats(statsData);
      setInstances(instancesData);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to load workflow dashboard:', err);
      setHasLoadError(true);
      // Only show toast on manual refresh, not on initial load
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Initial load - only run once on mount and when statusFilter changes
  useEffect(() => {
    loadDashboardData();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual refresh handler that shows toast on error
  const handleManualRefresh = async () => {
    setLoading(true);
    setHasLoadError(false);
    try {
      const [statsData, instancesData, templatesData] = await Promise.all([
        tcoWorkflowsAPI.getDashboard(),
        tcoWorkflowsAPI.listInstances({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        tcoWorkflowsAPI.listTemplates({ is_active: true }),
      ]);

      setStats(statsData);
      setInstances(instancesData);
      setTemplates(templatesData);
      toast({
        title: t('tcoReferrals.dashboard.refreshed', 'Refreshed'),
        description: t('tcoReferrals.dashboard.dataUpdated', 'Dashboard data updated'),
      });
    } catch (err) {
      console.error('Failed to refresh workflow dashboard:', err);
      setHasLoadError(true);
      toast({
        title: t('tcoReferrals.dashboard.error', 'Error'),
        description: t('tcoReferrals.dashboard.failedToRefresh', 'Failed to refresh workflow dashboard data'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return fmt.dateTime(dateStr);
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'waiting_response':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
      case 'failed':
      case 'cancelled':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
      case 'running':
        return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
      case 'paused':
        return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' };
      case 'waiting_response':
        return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
    }
  };

  // Get trigger type icon
  const getTriggerIcon = (triggerType: string) => {
    switch (triggerType) {
      case 'sdoh_threshold':
        return <Heart className="w-4 h-4 text-pink-500" />;
      case 'care_gap_detected':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'enrollment_milestone':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'ai_recommended':
        return <Bot className="w-4 h-4 text-purple-500" />;
      default:
        return <Play className="w-4 h-4 text-blue-500" />;
    }
  };

  // Filter instances by search
  const filteredInstances = instances.filter((instance) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      instance.member_name?.toLowerCase().includes(query) ||
      instance.template_name?.toLowerCase().includes(query)
    );
  });

  // Filter templates by search
  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.template_name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  });

  if (loading && !stats) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">{t('tcoReferrals.dashboard.loading', 'Loading workflow dashboard...')}</span>
      </div>
    );
  }

  if (hasLoadError && !stats) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('tcoReferrals.dashboard.unableToLoad', 'Unable to Load Dashboard')}</h3>
        <p className="text-gray-500 mb-4 text-center max-w-md">
          {t('tcoReferrals.dashboard.unableToLoadDesc', 'The TCO Workflow feature may not be enabled for your organization, or there was an error connecting to the server.')}
        </p>
        <Button variant="outline" onClick={loadDashboardData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('tcoReferrals.dashboard.tryAgain', 'Try Again')}
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Bot className="h-6 w-6 text-blue-600" />
              {t('tcoReferrals.dashboard.title', 'Workflow Dashboard')}
            </h1>
          )}
          <p className="text-gray-500 dark:text-gray-400">
            {t('tcoReferrals.dashboard.subtitle', 'Monitor and manage automated care coordination workflows')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('tcoReferrals.dashboard.refresh', 'Refresh')}
          </Button>
          <Button size="sm" onClick={() => {
            console.log('[TCOWorkflowDashboard] New Template button clicked');
            onCreateTemplate?.();
          }}>
            <Plus className="w-4 h-4 mr-1" />
            {t('tcoReferrals.dashboard.newTemplate', 'New Template')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-500">{t('tcoReferrals.dashboard.stats.inProgress', 'In Progress')}</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">{stats.total_in_progress}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-gray-500">{t('tcoReferrals.dashboard.stats.awaiting', 'Awaiting')}</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">{stats.total_waiting_response}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-500">{t('tcoReferrals.dashboard.stats.completed', 'Completed')}</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{stats.total_completed_30d}</div>
              <div className="text-xs text-gray-400">{t('tcoReferrals.dashboard.stats.last30Days', 'last 30 days')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-indigo-500" />
                <span className="text-sm text-gray-500">{t('tcoReferrals.dashboard.stats.referrals', 'Referrals')}</span>
              </div>
              <div className="text-2xl font-bold text-indigo-600">{stats.total_referrals_created_30d}</div>
              <div className="text-xs text-gray-400">{t('tcoReferrals.dashboard.stats.last30Days', 'last 30 days')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-teal-500" />
                <span className="text-sm text-gray-500">{t('tcoReferrals.dashboard.stats.success', 'Success')}</span>
              </div>
              <div className="text-2xl font-bold text-teal-600">
                {stats.success_rate?.toFixed(0) || 0}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {t('tcoReferrals.dashboard.tabs.overview', 'Overview')}
            </TabsTrigger>
            <TabsTrigger value="instances" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              {t('tcoReferrals.dashboard.tabs.instances', 'Instances')}
              {stats && stats.total_in_progress > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {stats.total_in_progress}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('tcoReferrals.dashboard.tabs.templates', 'Templates')}
            </TabsTrigger>
            {/* TRUS-686: org-level "when <event> then <actions>" rules. */}
            <TabsTrigger value="automations" className="flex items-center gap-2">
              <Wand2 className="w-4 h-4" />
              {t('tcoReferrals.dashboard.tabs.automations', 'Automations')}
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('tcoReferrals.dashboard.searchPlaceholder', 'Search...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('tcoReferrals.dashboard.recentActivity', 'Recent Activity')}</CardTitle>
              </CardHeader>
              <CardContent>
                {instances.slice(0, 5).map((instance) => (
                  <div
                    key={instance.id}
                    className="flex items-center justify-between py-3 border-b last:border-0 cursor-pointer hover:bg-gray-50"
                    onClick={() => onViewInstance?.(instance.id)}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(instance.status)}
                      <div>
                        <p className="font-medium text-sm">{instance.member_name || `Member #${instance.member_id}`}</p>
                        <p className="text-xs text-gray-500">{instance.template_name}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">{formatDate(instance.updated_at)}</div>
                  </div>
                ))}
                {instances.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('tcoReferrals.dashboard.noRecentActivity', 'No recent workflow activity')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Templates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('tcoReferrals.dashboard.activeTemplates', 'Active Templates')}</CardTitle>
              </CardHeader>
              <CardContent>
                {templates.slice(0, 5).map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between py-3 border-b last:border-0 cursor-pointer hover:bg-gray-50"
                    onClick={() => onEditTemplate?.(template.id)}
                  >
                    <div className="flex items-center gap-3">
                      {getTriggerIcon(template.trigger_type)}
                      <div>
                        <p className="font-medium text-sm">{template.template_name}</p>
                        <p className="text-xs text-gray-500">
                          {template.trigger_type.replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={template.auto_trigger_enabled ? 'border-green-500 text-green-700' : ''}
                    >
                      {template.auto_trigger_enabled ? 'Auto' : 'Manual'}
                    </Badge>
                  </div>
                ))}
                {templates.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('tcoReferrals.dashboard.noTemplatesConfigured', 'No workflow templates configured')}</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={onCreateTemplate}>
                      {t('tcoReferrals.dashboard.createTemplate', 'Create Template')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Performance by Trigger */}
          {stats?.workflows_by_trigger && Object.keys(stats.workflows_by_trigger).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('tcoReferrals.dashboard.workflowsByTrigger', 'Workflows by Trigger Type')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(stats.workflows_by_trigger).map(([triggerType, count]: [string, number]) => (
                    <div key={triggerType} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        {getTriggerIcon(triggerType)}
                        <span className="text-sm font-medium capitalize">
                          {triggerType.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-xl font-bold text-gray-900">{count}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Instances Tab */}
        <TabsContent value="instances" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('tcoReferrals.dashboard.workflowInstances', 'Workflow Instances')}</CardTitle>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border rounded-md"
                >
                  <option value="all">{t('tcoReferrals.dashboard.filters.allStatuses', 'All Statuses')}</option>
                  <option value="running">{t('tcoReferrals.dashboard.filters.running', 'Running')}</option>
                  <option value="waiting_response">{t('tcoReferrals.dashboard.filters.waitingResponse', 'Waiting Response')}</option>
                  <option value="paused">{t('tcoReferrals.dashboard.filters.paused', 'Paused')}</option>
                  <option value="completed">{t('tcoReferrals.dashboard.filters.completed', 'Completed')}</option>
                  <option value="failed">{t('tcoReferrals.dashboard.filters.failed', 'Failed')}</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredInstances.map((instance) => {
                  const statusColor = getStatusColor(instance.status);
                  return (
                    <div
                      key={instance.id}
                      className="p-4 border rounded-lg hover:shadow-sm cursor-pointer transition-shadow"
                      onClick={() => onViewInstance?.(instance.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getStatusIcon(instance.status)}
                          <div>
                            <p className="font-medium">
                              {instance.member_name || `Member #${instance.member_id}`}
                            </p>
                            <p className="text-sm text-gray-500">{instance.template_name}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>{t('tcoReferrals.dashboard.step', 'Step')} {instance.current_step} {t('tcoReferrals.dashboard.of', 'of')} {instance.total_steps}</span>
                              <span>{t('tcoReferrals.dashboard.started', 'Started')} {formatDate(instance.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`${statusColor.bg} ${statusColor.text} ${statusColor.border}`}
                          >
                            {instance.status.replace(/_/g, ' ')}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-indigo-600 h-1.5 rounded-full transition-all"
                            style={{
                              width: `${(instance.current_step / instance.total_steps) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredInstances.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-1">{t('tcoReferrals.dashboard.noWorkflowInstances', 'No workflow instances')}</p>
                    <p className="text-sm">
                      {statusFilter !== 'all'
                        ? t('tcoReferrals.dashboard.noFilteredWorkflows', 'No {{status}} workflows found', { status: statusFilter.replace(/_/g, ' ') })
                        : t('tcoReferrals.dashboard.workflowsWillAppear', 'Workflows will appear here when triggered')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('tcoReferrals.dashboard.workflowTemplates', 'Workflow Templates')}</CardTitle>
                <Button size="sm" onClick={onCreateTemplate}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t('tcoReferrals.dashboard.newTemplate', 'New Template')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="p-4 border rounded-lg hover:shadow-sm cursor-pointer transition-shadow"
                    onClick={() => onEditTemplate?.(template.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getTriggerIcon(template.trigger_type)}
                        <div>
                          <p className="font-medium">{template.template_name}</p>
                          <p className="text-sm text-gray-500">{template.description}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {template.trigger_type.replace(/_/g, ' ')}
                            </Badge>
                            {template.auto_trigger_enabled && (
                              <Badge className="text-xs bg-green-100 text-green-700">
                                <Zap className="w-3 h-3 mr-1" />
                                {t('tcoReferrals.dashboard.autoTrigger', 'Auto-trigger')}
                              </Badge>
                            )}
                            {template.auto_create_referral && (
                              <Badge className="text-xs bg-blue-100 text-blue-700">
                                <FileText className="w-3 h-3 mr-1" />
                                {t('tcoReferrals.dashboard.autoReferral', 'Auto-referral')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-1">{t('tcoReferrals.dashboard.noWorkflowTemplates', 'No workflow templates')}</p>
                    <p className="text-sm mb-4">{t('tcoReferrals.dashboard.createTemplatesDesc', 'Create templates to automate care coordination workflows')}</p>
                    <Button onClick={onCreateTemplate}>
                      <Plus className="w-4 h-4 mr-1" />
                      {t('tcoReferrals.dashboard.createFirstTemplate', 'Create First Template')}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automations Tab (TRUS-686) */}
        <TabsContent value="automations" className="mt-4">
          {activeTab === 'automations' && <AutomationRules />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TCOWorkflowDashboard;
